import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ExcelJS from 'exceljs';
import purchaseReportService from '../../../../services/purchaseReportService';
import PriceTrackingShell from './PriceTrackingShell';
import usePriceTrackingFilters from './usePriceTrackingFilters';
import { formatDateLocaleLima, formatMoney, formatPrice, formatPct, canOpenPurchaseProductDetail } from './shared';

export default function PriceTrackingHistoryPage() {
  const navigate = useNavigate();
  const { filters, updateFilter, buildParams, suppliers, products } = usePriceTrackingFilters();
  const [loading, setLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [ocRows, setOcRows] = useState([]);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [histRes, dashRes] = await Promise.all([
        purchaseReportService.getPriceTrackingHistory(buildParams()),
        purchaseReportService.getPriceTrackingDashboard(buildParams()),
      ]);
      if (histRes.success) {
        setHistoryRows(histRes.data?.rows || []);
      }
      if (dashRes.success) {
        setOcRows(dashRes.data?.oc_vs_invoice || []);
      }
      if (!histRes.success) {
        setToast({ isOpen: true, message: histRes.message || 'Error al cargar', type: 'error' });
      }
    } catch (error) {
      setToast({ isOpen: true, message: 'Error al cargar historial', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportExcel = async () => {
    if (!historyRows.length) {
      setToast({ isOpen: true, message: 'No hay datos para exportar', type: 'warning' });
      return;
    }
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Historial precios');
    sheet.addRow([
      'Fecha', 'Documento', 'Proveedor', 'SKU', 'Producto', 'Cantidad', 'U.M.',
      'Precio unit.', 'Precio neto', 'Subtotal', 'Precio OC', 'Δ% vs OC', 'Moneda',
    ]);
    historyRows.forEach((row) => {
      sheet.addRow([
        row.issue_date ? formatDateLocaleLima(row.issue_date) : '',
        row.document_code,
        row.supplier_name,
        row.sku,
        row.product_name,
        row.quantity,
        row.unit_of_measure,
        row.unit_price,
        row.net_unit_price,
        row.subtotal,
        row.po_unit_price ?? '',
        row.delta_vs_po_pct ?? '',
        row.currency,
      ]);
    });
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `historial-precios-${filters.dateFrom}-${filters.dateTo}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PriceTrackingShell
      title="Historial de precios"
      subtitle="Línea a línea de cada compra facturada y diferencias OC vs factura."
      filters={filters}
      updateFilter={updateFilter}
      suppliers={suppliers}
      products={products}
      onQuery={loadData}
      loading={loading}
      toast={toast}
      onToastClose={() => setToast({ ...toast, isOpen: false })}
      extraActions={historyRows.length > 0 ? (
        <button
          type="button"
          onClick={exportExcel}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium"
        >
          Exportar Excel
        </button>
      ) : null}
    >
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm overflow-x-auto border border-gray-200">
          {loading ? (
            <p className="p-8 text-center text-gray-500">Cargando...</p>
          ) : historyRows.length === 0 ? (
            <p className="p-8 text-center text-gray-500">Sin líneas en el período.</p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs text-gray-500 uppercase">Fecha</th>
                  <th className="px-3 py-3 text-left text-xs text-gray-500 uppercase">Doc.</th>
                  <th className="px-3 py-3 text-left text-xs text-gray-500 uppercase">Proveedor</th>
                  <th className="px-3 py-3 text-left text-xs text-gray-500 uppercase">Producto</th>
                  <th className="px-3 py-3 text-right text-xs text-gray-500 uppercase">Cant.</th>
                  <th className="px-3 py-3 text-right text-xs text-gray-500 uppercase">P. neto</th>
                  <th className="px-3 py-3 text-right text-xs text-gray-500 uppercase">Subtotal</th>
                  <th className="px-3 py-3 text-right text-xs text-gray-500 uppercase">Δ OC</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {historyRows.map((row) => (
                  <tr
                    key={`${row.document_code}-${row.row_number}`}
                    className={canOpenPurchaseProductDetail(row) ? 'hover:bg-gray-50 cursor-pointer' : 'hover:bg-gray-50'}
                    onClick={() => {
                      if (!canOpenPurchaseProductDetail(row)) return;
                      navigate(`/compras/reports/price-tracking/products/${row.product_id ?? row.item_id}`);
                    }}
                  >
                    <td className="px-3 py-3">{formatDateLocaleLima(row.issue_date)}</td>
                    <td className="px-3 py-3 font-mono text-teal-700 text-xs">{row.document_code}</td>
                    <td className="px-3 py-3">{row.supplier_name}</td>
                    <td className="px-3 py-3">{row.product_name}</td>
                    <td className="px-3 py-3 text-right">{row.quantity}</td>
                    <td className="px-3 py-3 text-right font-medium">{formatPrice(row.net_unit_price)}</td>
                    <td className="px-3 py-3 text-right">{formatMoney(row.subtotal, row.currency)}</td>
                    <td className="px-3 py-3 text-right text-amber-700">{formatPct(row.delta_vs_po_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {ocRows.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
            <div className="px-5 py-4 border-b">
              <h2 className="font-semibold text-gray-800">Diferencia OC vs factura</h2>
              <p className="text-sm text-gray-500">Líneas con variación ≥ 1% respecto a la orden de compra</p>
            </div>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Documento</th>
                  <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Producto</th>
                  <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Precio OC</th>
                  <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Precio factura</th>
                  <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Δ%</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ocRows.map((row, i) => (
                  <tr key={`${row.document_code}-${i}`} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-teal-700 text-xs">{row.document_code}</td>
                    <td className="px-3 py-2">{row.product_name}</td>
                    <td className="px-3 py-2 text-right">{formatPrice(row.po_unit_price)}</td>
                    <td className="px-3 py-2 text-right">{formatPrice(row.invoice_unit_price)}</td>
                    <td className="px-3 py-2 text-right font-medium text-amber-700">{formatPct(row.delta_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PriceTrackingShell>
  );
}
